import { parseQuery } from '@calendly/http-tools'

import { setGetParams } from 'actions/core/navigation'
import { isValidUUID } from 'shared/utils/uuid'

import {
  clearStoredAiRailThreadId,
  getStoredAiRailThreadId,
  setStoredAiRailThreadId,
} from './ai_rail_thread_storage'
import { AI_CHAT_DISCOVERY_VARIANT } from './constants'

export { clearStoredAiRailThreadId, setStoredAiRailThreadId }

const PHASE_1_DISCOVERY_VARIANT_SET = new Set([
  AI_CHAT_DISCOVERY_VARIANT.CONTROL,
  AI_CHAT_DISCOVERY_VARIANT.BUTTON_ONLY,
  AI_CHAT_DISCOVERY_VARIANT.TOP_BUTTON,
])

/**
 * @param {string | null | undefined} variant — raw `useVariant` value
 * @returns {boolean} false while bootstrap experiment values are still loading
 */
export const isAiChatDiscoveryVariantResolved = (variant) => (
  variant != null && variant !== ''
)

/**
 * Maps experiment variant to effective variant for the current phase.
 * @param {string | null | undefined} variant — raw `useVariant` value
 * @returns {typeof AI_CHAT_DISCOVERY_VARIANT.CONTROL | typeof AI_CHAT_DISCOVERY_VARIANT.BUTTON_ONLY}
 */
export const getAiChatDiscoveryEffectiveVariant = (variant) => {
  if (!variant) return AI_CHAT_DISCOVERY_VARIANT.CONTROL
  if (PHASE_1_DISCOVERY_VARIANT_SET.has(variant)) return variant
  return AI_CHAT_DISCOVERY_VARIANT.CONTROL
}

/**
 * Clears meetings list selection query params when present — same shape as
 * `clearActiveMeeting` in meetings_page.js.
 *
 * Call before {@link useRightRail}'s `close()` when leaving the AI chat rail so the meetings
 * page does not re-open `meeting_details` from `selected` / `share_id`.
 *
 * @param {import('redux').Dispatch} dispatch
 * @param {string} search - `location.search` from react-router
 */
export const clearMeetingsSelectionFromUrlIfPresent = (dispatch, search) => {
  const { selected, share_id: shareId } = parseQuery(search)
  const hadSelection = (selected != null && selected !== '') || (shareId != null && shareId !== '')
  if (hadSelection) {
    dispatch(setGetParams({ selected: null, share_id: null }))
  }
}

/**
 * Merge explicit pane props with a stored rail thread id when props omit a valid threadId.
 * @param {Object} [props]
 * @returns {Object | undefined} props for `openOrReplace('ai_chat', …)` / `replaceAndClearStack`
 */
export const mergeAiChatPanePropsWithStoredThread = (props = {}) => {
  const threadId = isValidUUID(props.threadId)
    ? props.threadId
    : getStoredAiRailThreadId()

  if (!threadId) {
    return Object.keys(props).length > 0 ? props : undefined
  }

  return { ...props, threadId }
}

/**
 * @param {(name: string, props?: Object) => void} openFn — `openOrReplace` or `replaceAndClearStack`
 * @param {Object} [props]
 */
export const openAiChatRailPane = (openFn, props) => {
  const merged = mergeAiChatPanePropsWithStoredThread(props ?? {})
  if (merged) {
    openFn('ai_chat', merged)
  } else {
    openFn('ai_chat')
  }
}

/** Persist pane thread id before closing so reopen can resume (close does not clear storage). */
const persistAiRailThreadIdBeforeClose = (current) => {
  const threadId = current?.paneProps?.threadId
  if (isValidUUID(threadId)) {
    setStoredAiRailThreadId(threadId)
  }
}

/**
 * @param {Function} closeFn
 * @param {Object | null} current — `useRightRail().current`
 */
export const closeAiChatRailPane = (closeFn, current) => {
  persistAiRailThreadIdBeforeClose(current)
  closeFn()
}
